import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Button, InputBase } from '@material-ui/core'
import { Theme, makeStyles } from '@material-ui/core/styles'
import { NoteType } from 'models/projectNote'
import React, { useMemo } from 'react'
import { HiFunnel, HiPlus, HiXMark } from 'shared/HarmonyIcon'
import HarmonySelect from 'shared/HarmonySelect'
import { HARMONY, HX, RADIUS } from 'shared/harmonyTokens'
import { formatLocalDate } from 'shared/utils/dateUtils'

export const PINNED_FILTER_VALUE = 'pinned'
export const TASK_TYPE_URL_PARAM = 'taskType'
export const TASK_TYPE_ALL_VALUE = 'All'

export interface TasksFilter {
    nameSearch: string
    creatorId: string
    noteType: string
    dateFrom: string
    contentItemId: string
}

export interface TasksFilterUser {
    id: string
    profileInfo?: {
        firstName?: string
        lastName?: string
        imageUrl?: string
    }
}

interface ProjectTasksFilterProps {
    filter: TasksFilter
    onFilterChange: (filter: TasksFilter) => void
    onAddTask: () => void
    users: TasksFilterUser[]
    isFiltersVisible: boolean
    onToggleFilters: () => void
    /** Human-readable element label from the workspace (e.g. "Family 030130 - SECTION").
     *  When present a chip is shown indicating the board is scoped to that element. */
    elementLabel?: string
    /** NoteType from the task indicator that opened this board (e.g. NoteType.Family).
     *  Controls which type filter options are shown: ProductType → [General, Section, Product],
     *  Family → [General, Section], others → [General only]. */
    pinnedNoteType?: string
    /** When true, adds a "Pinned Article" option that filters tasks to the specific
     *  article/paragraph that opened this board (by contentItemId). */
    hasPinnedOption?: boolean
}

const GENERAL_OPTION = { value: NoteType.GeneralProject, label: 'General Task' }
const SECTION_OPTION = { value: NoteType.Family, label: 'Section Task' }
const PRODUCT_OPTION = { value: NoteType.ProductType, label: 'Product Task' }
const PINNED_OPTION = { value: PINNED_FILTER_VALUE, label: 'Pinned Task' }

const useStyles = makeStyles((theme: Theme) => ({
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.25),
        padding: theme.spacing(2, 3),
        background: HARMONY.surfaceBg,
        borderBottom: `1px solid ${HX.borderSoft}`,
        flexWrap: 'wrap',
        flexShrink: 0,
    },
    // Add Task — filled rounded rect (9px, per design)
    addBtn: {
        '&&': {
            height: pxToRem(38),
            padding: theme.spacing(0, 2),
            borderRadius: pxToRem(9),
            fontSize: pxToRem(13.5),
            whiteSpace: 'nowrap',
            '&:active': {
                transform: 'translateY(1px)',
            },
        },
    },
    filterSection: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginLeft: 'auto',
        flexWrap: 'wrap',
    },
    // Filters toggle — outline rounded rect (9px, per design)
    filterToggle: {
        '&&': {
            height: pxToRem(38),
            padding: theme.spacing(0, 1.75),
            borderRadius: pxToRem(9),
            border: `1px solid #D5DCE8`,
            background: '#FBFCFE',
            color: '#5A6678',
            fontSize: pxToRem(13),
            fontWeight: 600,
            letterSpacing: '0.02em',
            textTransform: 'none',
            gap: theme.spacing(0.75),
            '&:hover': {
                background: HX.primaryTint,
                borderColor: HX.primary,
                color: HX.primaryDeep,
            },
        },
    },
    filterToggleActive: {
        '&&': {
            background: HX.primary,
            borderColor: HX.primary,
            color: HARMONY.textInverse,
            '&:hover': {
                background: HX.primaryHover,
                borderColor: HX.primaryHover,
                color: HARMONY.textInverse,
            },
        },
    },
    filterInputs: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
    },
    searchInput: {
        height: pxToRem(38),
        padding: theme.spacing(0, 1.75),
        border: `1px solid ${HARMONY.borderLight}`,
        borderRadius: pxToRem(RADIUS.lg),
        background: HARMONY.inputBg,
        fontSize: pxToRem(12.5),
        fontWeight: 500,
        color: HX.ink,
        width: pxToRem(158),
        '&:hover': { borderColor: HX.border },
        '&.Mui-focused': {
            borderColor: HX.primaryDeep,
            boxShadow: `0 0 0 3px ${HX.primaryTint}`,
        },
        '& input::placeholder': { color: HX.ink3, fontWeight: 400 },
    },
    dateInput: {
        height: pxToRem(38),
        padding: `0 ${theme.spacing(1.75)}px`,
        border: `1px solid ${HARMONY.borderLight}`,
        borderRadius: pxToRem(RADIUS.lg),
        background: HARMONY.inputBg,
        fontSize: pxToRem(12.5),
        fontWeight: 500,
        color: HX.ink,
        width: pxToRem(148),
        fontFamily: 'inherit',
        cursor: 'pointer',
        '&:hover': { borderColor: HX.border },
        '&.Mui-focused': {
            borderColor: HX.primaryDeep,
            boxShadow: `0 0 0 3px ${HX.primaryTint}`,
        },
    },
    dateInputError: {
        borderColor: HX.error,
        '&:hover': { borderColor: HX.error },
        '&.Mui-focused': {
            borderColor: HX.error,
            boxShadow: '0 0 0 3px rgba(216,49,72,0.18)',
        },
    },
    clearBtn: {
        '&&': {
            height: pxToRem(38),
            padding: theme.spacing(0, 1.75),
            borderRadius: pxToRem(9),
            border: `1px solid ${HX.borderSoft}`,
            background: HARMONY.surfaceBg,
            color: HX.ink2,
            fontSize: pxToRem(13),
            fontWeight: 600,
            whiteSpace: 'nowrap',
            transition: 'border-color 130ms ease, color 130ms ease',
            '&:hover': {
                background: HARMONY.surfaceBg,
                borderColor: HX.danger,
                color: HX.danger,
            },
        },
    },
}))

/** Toolbar for the Project Tasks board: search/filter controls and the Add Task button. */
const ProjectTasksFilter = ({
    filter,
    onFilterChange,
    onAddTask,
    users,
    isFiltersVisible,
    onToggleFilters,
    elementLabel,
    pinnedNoteType,
    hasPinnedOption,
}: ProjectTasksFilterProps) => {
    const classes = useStyles()
    const today = formatLocalDate(new Date()) ?? ''
    const hasDateError = !!filter.dateFrom && filter.dateFrom > today

    const isActive = !!(
        filter.nameSearch ||
        filter.creatorId ||
        filter.noteType ||
        filter.dateFrom
    )

    const noteTypeOptions = useMemo(() => {
        const allOption = { value: '', label: 'All Task Types' }

        // Order: General → Section → Product (from least to most specific).
        // ProductType board shows all three; Family board shows General + Section only.
        // No pinned context (project-level board) also shows all three — the board aggregates all types.
        const contextOptionsMap: Record<string, { value: string; label: string }[]> = {
            [NoteType.ProductType]: [GENERAL_OPTION, SECTION_OPTION, PRODUCT_OPTION],
            [NoteType.Family]: [GENERAL_OPTION, SECTION_OPTION],
        }
        const contextOptions =
            contextOptionsMap[pinnedNoteType ?? ''] ?? contextOptionsMap[NoteType.ProductType]
        const options = [allOption, ...contextOptions]
        if (hasPinnedOption) options.push(PINNED_OPTION)
        return options
    }, [pinnedNoteType, hasPinnedOption])

    const creatorOptions = [
        { value: '', label: 'Anyone' },
        ...users.map((u) => ({
            value: u.id,
            label:
                [u.profileInfo?.firstName, u.profileInfo?.lastName]
                    .filter(Boolean)
                    .join(' ') || u.id,
        })),
    ]

    return (
        <div className={classes.toolbar}>
            <Button
                variant="contained"
                color="primary"
                className={classes.addBtn}
                onClick={onAddTask}
                startIcon={<HiPlus size={15} strokeWidth={2.2} />}
            >
                Add Task
            </Button>

            <div className={classes.filterSection}>
                <Button
                    className={`${classes.filterToggle}${
                        isFiltersVisible ? ` ${classes.filterToggleActive}` : ''
                    }`}
                    onClick={onToggleFilters}
                    startIcon={<HiFunnel size={15} />}
                >
                    Filters
                </Button>

                {isFiltersVisible && (
                    <div className={classes.filterInputs}>
                        <InputBase
                            className={classes.searchInput}
                            placeholder="Search tasks"
                            value={filter.nameSearch}
                            onChange={(e) =>
                                onFilterChange({
                                    ...filter,
                                    nameSearch: e.target.value,
                                })
                            }
                        />

                        <HarmonySelect
                            value={filter.creatorId}
                            options={creatorOptions}
                            placeholder="Anyone"
                            onChange={(val) =>
                                onFilterChange({ ...filter, creatorId: val })
                            }
                            width={160}
                        />

                        <HarmonySelect
                            value={filter.noteType}
                            options={noteTypeOptions}
                            placeholder={elementLabel || 'All Task Types'}
                            onChange={(val) =>
                                onFilterChange({ ...filter, noteType: val })
                            }
                            width={180}
                        />

                        <InputBase
                            className={`${classes.dateInput}${
                                hasDateError ? ` ${classes.dateInputError}` : ''
                            }`}
                            type="date"
                            value={filter.dateFrom}
                            inputProps={{ max: today }}
                            onChange={(e) =>
                                onFilterChange({
                                    ...filter,
                                    dateFrom: e.target.value,
                                })
                            }
                        />
                    </div>
                )}

                {isFiltersVisible && isActive && (
                    <Button
                        className={classes.clearBtn}
                        startIcon={<HiXMark size={15} strokeWidth={2} />}
                        onClick={() =>
                            onFilterChange({
                                nameSearch: '',
                                creatorId: '',
                                noteType: '',
                                dateFrom: '',
                                contentItemId: '',
                            })
                        }
                    >
                        Clear filters
                    </Button>
                )}
            </div>
        </div>
    )
}

export default ProjectTasksFilter
